// apps/web/src/lib/confirm.tsx
// Promise-based confirm dialog. `const ok = await confirm({ message, danger })`
// renders a branded dialog (via the responsive SheetShell) and resolves the user's
// choice — replacing the native browser confirm().
import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from "react";
import { useT } from "./i18n";
import { SheetShell } from "../components/SheetShell";
import { Button } from "../components/ui/Button";

export interface ConfirmOptions {
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
}

type ConfirmFn = (opts: ConfirmOptions) => Promise<boolean>;
const ConfirmCtx = createContext<ConfirmFn | null>(null);

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const t = useT();
  const [opts, setOpts] = useState<ConfirmOptions | null>(null);
  const resolveRef = useRef<(v: boolean) => void>(() => {});

  const confirm = useCallback<ConfirmFn>((o) => {
    setOpts(o);
    return new Promise<boolean>((resolve) => { resolveRef.current = resolve; });
  }, []);

  function close(result: boolean) {
    resolveRef.current(result);
    setOpts(null);
  }

  return (
    <ConfirmCtx.Provider value={confirm}>
      {children}
      {opts && (
        <SheetShell onClose={() => close(false)} className="sm:max-w-sm">
          <div className="space-y-4 py-2">
            <p className="text-base text-fg">{opts.message}</p>
            <div className="flex gap-2">
              <Button variant="secondary" fullWidth onClick={() => close(false)}>
                {opts.cancelLabel ?? t("common.cancel")}
              </Button>
              <Button variant={opts.danger ? "danger" : "primary"} fullWidth onClick={() => close(true)}>
                {opts.confirmLabel ?? t("common.confirm")}
              </Button>
            </div>
          </div>
        </SheetShell>
      )}
    </ConfirmCtx.Provider>
  );
}

export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmCtx);
  if (!ctx) throw new Error("useConfirm must be used within ConfirmProvider");
  return ctx;
}
